import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { LotCard } from "@/components/LotCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, SquaresFour, Table as TableIcon, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { inr, num, resolveImg, imgFallback } from "@/lib/api";
import { VEHICLE_TYPES, BRAND_TYPES } from "@/lib/config";
import { Link } from "react-router-dom";

const PAGE_SIZE = 9;

export default function Lots() {
  const [params, setParams] = useSearchParams();
  const [lots, setLots] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ categories: [], states: [] });
  const [view, setView] = useState("grid");

  const [q, setQ] = useState(params.get("q") || "");
  const vehicle_type = params.get("vehicle_type") || "all";
  const brand_type = params.get("brand_type") || "all";
  const state = params.get("state") || "all";
  const sort = params.get("sort") || "recent";
  const page = parseInt(params.get("page") || "1", 10);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v && v !== "all") next.set(k, v);
      else next.delete(k);
    });
    if (!("page" in patch)) next.set("page", "1");
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lots", {
        params: { q: params.get("q") || undefined, vehicle_type: vehicle_type !== "all" ? vehicle_type : undefined, brand_type: brand_type !== "all" ? brand_type : undefined, state: state !== "all" ? state : undefined, sort, page, page_size: PAGE_SIZE },
      });
      setLots(data.lots);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [params, vehicle_type, brand_type, state, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/lots/filters").then(({ data }) => setFilters(data)).catch(() => {}); }, []);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Marketplace</span>
        <h1 className="font-display text-3xl font-black tracking-tight text-navy lg:text-4xl">Bulk liquidation lots</h1>
        <p className="text-muted-foreground">{total} verified {total === 1 ? "lot" : "lots"} available · all prices in ₹ INR</p>
      </div>

      {/* Filter bar */}
      <div className="mt-8 flex flex-col gap-3 rounded-xl border border-border bg-white p-4 shadow-sm lg:flex-row lg:items-center">
        <form
          onSubmit={(e) => { e.preventDefault(); update({ q }); }}
          className="relative flex-1"
        >
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            data-testid="lots-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, brand, keyword..."
            className="rounded-sm border-border bg-white pl-10"
          />
        </form>
        <Select value={vehicle_type} onValueChange={(v) => update({ vehicle_type: v })}>
          <SelectTrigger className="w-full rounded-sm border-border bg-white lg:w-44" data-testid="filter-vehicle-type"><SelectValue placeholder="Vehicle type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vehicle types</SelectItem>
            {VEHICLE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={brand_type} onValueChange={(v) => update({ brand_type: v })}>
          <SelectTrigger className="w-full rounded-sm border-border bg-white lg:w-44" data-testid="filter-brand-type"><SelectValue placeholder="Brand type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brand types</SelectItem>
            {BRAND_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={(v) => update({ state: v })}>
          <SelectTrigger className="w-full rounded-sm border-border bg-white lg:w-44" data-testid="filter-state"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {filters.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => update({ sort: v })}>
          <SelectTrigger className="w-full rounded-sm border-border bg-white lg:w-44" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most recent</SelectItem>
            <SelectItem value="price_low">Price: low to high</SelectItem>
            <SelectItem value="price_high">Price: high to low</SelectItem>
            <SelectItem value="value">MRP value</SelectItem>
            <SelectItem value="units">Unit count</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1 rounded-sm border border-border bg-white p-1">
          <button onClick={() => setView("grid")} data-testid="view-grid" className={`rounded-sm p-2 ${view === "grid" ? "bg-navy text-white" : "text-navy"}`}><SquaresFour size={18} /></button>
          <button onClick={() => setView("table")} data-testid="view-table" className={`rounded-sm p-2 ${view === "table" ? "bg-navy text-white" : "text-navy"}`}><TableIcon size={18} /></button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="mt-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-navy/20 border-t-brand" /></div>
      ) : lots.length === 0 ? (
        <div className="mt-16 rounded-sm border border-dashed border-border p-16 text-center">
          <p className="font-display text-lg font-semibold text-navy">No lots match your filters</p>
          <p className="mt-1 text-muted-foreground">Try clearing filters or broadening your search.</p>
          <Button variant="outline" className="mt-4 rounded-sm" onClick={() => setParams({})} data-testid="clear-filters">Clear all filters</Button>
        </div>
      ) : view === "grid" ? (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {lots.map((lot, i) => <LotCard key={lot.id} lot={lot} index={i} />)}
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-sm border border-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-navy text-white">
              <tr className="text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Lot</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3 text-right">Units</th>
                <th className="px-4 py-3 text-right">MRP Value</th>
                <th className="px-4 py-3 text-right">Asking</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lots.map((lot) => (
                <tr key={lot.id} className="bg-white hover:bg-navy-50" data-testid={`lot-row-${lot.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={resolveImg(lot.image_ids?.[0])} onError={imgFallback} alt="" className="h-10 w-14 rounded-sm object-cover" />
                      <span className="font-medium text-navy line-clamp-1 max-w-[220px]">{lot.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{lot.vehicle_type || lot.category}{lot.brand_type ? ` · ${lot.brand_type}` : ""}</td>
                  <td className="px-4 py-3 text-muted-foreground">{lot.city}, {lot.state}</td>
                  <td className="stat px-4 py-3 text-right">{num(lot.total_units)}</td>
                  <td className="stat px-4 py-3 text-right">{inr(lot.mrp_value)}</td>
                  <td className="stat px-4 py-3 text-right font-bold text-brand">{inr(lot.asking_price)}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/lots/${lot.id}`} className="font-semibold text-navy hover:text-brand">View →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} data-testid="page-prev"><ArrowLeft /></Button>
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} onClick={() => update({ page: String(i + 1) })} data-testid={`page-${i + 1}`} className={`stat h-9 w-9 rounded-sm text-sm ${page === i + 1 ? "bg-navy text-white" : "border border-border bg-white text-navy hover:bg-navy-50"}`}>{i + 1}</button>
          ))}
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })} data-testid="page-next"><ArrowRight /></Button>
        </div>
      )}
    </div>
  );
}
